import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

export interface ActionItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  onClick: (e?: React.MouseEvent) => void;
  danger?: boolean;
}

export interface RowActionsProps {
  actions: (ActionItem | null | undefined | false | string | number)[];
  className?: string;
}

const RowActions: React.FC<RowActionsProps> = ({ actions, className = '' }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [portalPos, setPortalPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!dropdownOpen) return;

    const handleOutsideClick = () => {
      setDropdownOpen(false);
    };

    const handleScrollOrResize = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const menuWidth = 190;
        const left = Math.max(12, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 12));
        const top = rect.bottom + 6;
        setPortalPos({ top, left });
      }
    };

    document.addEventListener('click', handleOutsideClick);
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      document.removeEventListener('click', handleOutsideClick);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [dropdownOpen]);

  const filteredActions = (actions || []).filter(
    (a): a is ActionItem => Boolean(a && typeof a === 'object' && typeof a.onClick === 'function')
  );

  if (filteredActions.length === 0) return null;

  // RULE:
  // If 1 or 2 actions -> show directly as icon buttons.
  // If 3+ actions -> show first 2 primary actions directly + 3-dot icon button.
  // All remaining actions (3rd, 4th, 5th...) go inside the 3-dot dropdown menu.
  const hasMore = filteredActions.length > 2;
  const visibleActions = hasMore ? filteredActions.slice(0, 2) : filteredActions;
  const menuActions = hasMore ? filteredActions.slice(2) : [];

  const handleToggleMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!dropdownOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const menuWidth = 190;
      const left = Math.max(12, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 12));
      const top = rect.bottom + 6;
      setPortalPos({ top, left });
    }
    setDropdownOpen((prev) => !prev);
  };

  return (
    <div
      className={`obs-row-actions d-inline-flex align-items-center justify-content-end gap-1.5 ${className}`}
      onClick={(e) => e.stopPropagation()}
    >
      {visibleActions.map((act) => (
        <button
          key={act.id}
          type="button"
          aria-label={act.label}
          title={act.label}
          onClick={(e) => {
            e.stopPropagation();
            act.onClick(e);
          }}
          className={`obs-action-btn ${act.danger ? 'obs-action-btn-danger' : ''}`}
        >
          {act.icon}
        </button>
      ))}

      {hasMore && (
        <>
          <button
            ref={triggerRef}
            type="button"
            aria-label="More actions"
            title="More actions"
            aria-expanded={dropdownOpen}
            onClick={handleToggleMenu}
            className={`obs-action-btn ${dropdownOpen ? 'active' : ''}`}
          >
            {/* 3-Dot Ellipsis Vertical Icon matching CoHostZone */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="1.5" />
              <circle cx="12" cy="5" r="1.5" />
              <circle cx="12" cy="19" r="1.5" />
            </svg>
          </button>

          {dropdownOpen &&
            portalPos &&
            createPortal(
              <div
                className="obs-row-actions-dropdown"
                style={{
                  position: 'fixed',
                  top: portalPos.top,
                  left: portalPos.left,
                  width: 190,
                  zIndex: 1099,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {menuActions.map((act) => (
                  <button
                    key={act.id}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDropdownOpen(false);
                      act.onClick(e);
                    }}
                    className={`obs-row-actions-item ${act.danger ? 'obs-action-danger' : ''}`}
                  >
                    {act.icon && <span className="obs-row-actions-icon">{act.icon}</span>}
                    <span>{act.label}</span>
                  </button>
                ))}
              </div>,
              document.body
            )}
        </>
      )}
    </div>
  );
};

export default RowActions;
